<template>
  <div>
    <VSpinner v-if="isDomainGateLoading" class="centered-block" />
    <VFallbackText v-else-if="isDomainBlocked" class="centered-block">
      {{ AUTOMATED_EMAIL_DOMAIN_GATE_MESSAGE }}
    </VFallbackText>
    <VFallbackText v-else-if="!hasPatchId" class="centered-block">
      Select an area to manage automated emails.
    </VFallbackText>
    <VTabs
      v-else
      :tabs="tabs"
      toggle-manually
      class="listings-show"
      @on-tab-click="activateTabByIndex"
    >
      <template
        v-for="(tab, index) in visibleTabs"
        #[`tab-${index}`]="{ active }"
        :key="tab.key"
      >
        <keep-alive>
          <AutomatedEmailSettings
            v-if="active && tab.key === EAutomatedEmailTab.SETTINGS"
          />
          <AutomatedEmailOutbox v-else-if="active && tab.key === EAutomatedEmailTab.OUTBOX" />
          <AutomatedEmailSent v-else-if="active && tab.key === EAutomatedEmailTab.SENT" />
          <AutomatedEmailDashboard
            v-else-if="active && tab.key === EAutomatedEmailTab.DASHBOARD"
          />
        </keep-alive>
      </template>
    </VTabs>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { ComputedRef, Ref } from 'vue';
  import type { ITab } from '~/misc/v-tabs/types';
  import type { TAutomatedEmailTab } from '~/modules/opportunities/automated-email/show/types';
  import type { TAutomatedEmailDomainStatus } from '~/modules/opportunities/automated-email/types';
  import type { TAuthStore } from '~/modules/auth/store/types';
  import { EAutomatedEmailTab } from '~/modules/opportunities/automated-email/show/types';

  // Config
  import {
    AUTOMATED_EMAIL_TABS,
    AUTOMATED_EMAIL_DOMAIN_GATE_MESSAGE,
  } from '~/modules/opportunities/automated-email/show/config';

  // Composables
  import { useAutomatedEmailPatch } from '~/modules/opportunities/automated-email/composables/use-automated-email-patch';

  // Store
  import { useAuthStore } from '~/modules/auth/store';

  // Services
  import { AuthAccess } from '~/misc/auth/services/access';

  // Components
  import VTabs from '~/misc/v-tabs';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import VSpinner from '~/misc/v-spinner/VSpinner.vue';
  import AutomatedEmailSettings from '~/modules/opportunities/automated-email/settings/AutomatedEmailSettings.vue';
  import AutomatedEmailOutbox from '~/modules/opportunities/automated-email/outbox/AutomatedEmailOutbox.vue';
  import AutomatedEmailSent from '~/modules/opportunities/automated-email/sent/AutomatedEmailSent.vue';
  import AutomatedEmailDashboard from '~/modules/opportunities/automated-email/dashboard/AutomatedEmailDashboard.vue';

  // Models
  import { AutomatedEmailDomainModel } from '~/modules/opportunities/automated-email/models/automated-email-domain.model';
  import { TriggersModel } from '~/modules/opportunities/automated-email/settings/models/triggers';
  import { ensureAutomatedEmailTriggersAndTemplates } from '~/modules/opportunities/automated-email/settings/utils/ensure-automated-email-triggers-templates';
  import {
    shouldBlockAutomatedEmailForDomainStatus,
    shouldBlockAutomatedEmailForDomainStatusError,
  } from '~/modules/opportunities/automated-email/show/utils/domain-gate';

  /** Production runs the domain gate; dev (`import.meta.env.DEV`) skips it. */
  const skipDomainStatusGate: boolean = import.meta.env.DEV;

  const { patchId, hasPatchId, saleType } = useAutomatedEmailPatch();
  const authStore: TAuthStore = useAuthStore();

  const canManageSettings: ComputedRef<boolean> = computed<boolean>(
    () => !!authStore.user && AuthAccess.canAdministrate(authStore.user)
  );

  const visibleTabs: ComputedRef<TAutomatedEmailTab[]> = computed<TAutomatedEmailTab[]>(() =>
    AUTOMATED_EMAIL_TABS.filter(
      (tab: TAutomatedEmailTab): boolean =>
        tab.key !== EAutomatedEmailTab.SETTINGS || canManageSettings.value
    )
  );

  const isDomainGateLoading: Ref<boolean> = ref<boolean>(!skipDomainStatusGate);
  const isDomainBlocked: Ref<boolean> = ref<boolean>(false);

  const activeTabIndex: Ref<number> = ref<number>(0);

  const setIsDomainGateLoading = (value: boolean): void => {
    isDomainGateLoading.value = value;
  };

  const setIsDomainBlocked = (value: boolean): void => {
    isDomainBlocked.value = value;
  };

  const setActiveTabIndex = (index: number): void => {
    activeTabIndex.value = index;
  };

  const tabs: ComputedRef<ITab[]> = computed<ITab[]>(() =>
    visibleTabs.value.map(
      (tab: TAutomatedEmailTab, index: number): ITab => ({
        label: tab.label,
        active: activeTabIndex.value === index,
      })
    )
  );

  const activateTabByIndex = (index: number): void => {
    setActiveTabIndex(index);
  };

  const bootstrapAutomatedEmailTriggersAndTemplates = async (): Promise<void> => {
    if (!canManageSettings.value || patchId.value === undefined) {
      return;
    }

    try {
      await ensureAutomatedEmailTriggersAndTemplates(
        new TriggersModel(),
        patchId.value,
        saleType.value
      );
    } catch {
      // Settings `fetchTriggers` retries; avoid blocking the page (network / feature flag).
    }
  };

  const runBootstrapsAfterDomainOk = (): void => {
    bootstrapAutomatedEmailTriggersAndTemplates().catch(() => {});
  };

  const initializeDomainGate = async (): Promise<void> => {
    try {
      const status: TAutomatedEmailDomainStatus =
        await new AutomatedEmailDomainModel().getStatus();

      setIsDomainBlocked(shouldBlockAutomatedEmailForDomainStatus(status));

      if (!isDomainBlocked.value) {
        runBootstrapsAfterDomainOk();
      }
    } catch (error: unknown) {
      const blocked: boolean = shouldBlockAutomatedEmailForDomainStatusError(error);

      setIsDomainBlocked(blocked);

      if (!blocked) {
        runBootstrapsAfterDomainOk();
      }
    } finally {
      setIsDomainGateLoading(false);
    }
  };

  watch(
    [patchId, saleType],
    ([nextPatchId, nextSaleType], [previousPatchId, previousSaleType]) => {
      if (
        nextPatchId === undefined ||
        (nextPatchId === previousPatchId && nextSaleType === previousSaleType) ||
        isDomainBlocked.value ||
        isDomainGateLoading.value
      ) {
        return;
      }

      bootstrapAutomatedEmailTriggersAndTemplates().catch(() => {});
    }
  );

  onMounted((): void => {
    if (skipDomainStatusGate) {
      setIsDomainGateLoading(false);
      runBootstrapsAfterDomainOk();

      return;
    }

    initializeDomainGate().catch((): void => {});
  });
</script>
